import { useCallback, useEffect, useState } from 'react'
import type { HocuspocusProvider } from '@hocuspocus/provider'
import { onRoomsRecover } from './connectivity'

/**
 * Honest connection health for a CRDT room, plus self-healing when the member comes back.
 *
 * WHY THIS EXISTS — the provider's `status` is not the truth the member needs. It reads
 * 'connected' for as long as the socket OBJECT has not been closed, so a socket that died
 * silently (dropped WS, slept laptop, proxy that cut the tunnel) keeps a green "Live" indicator
 * over content that no longer receives anything. Reproduced in a real browser: with the page's
 * socket taken down, a console-agent edit applied through /v1 never appeared and the chip still
 * read "Live · 1 editing". The member sees stale content behind a healthy light, has no reason to
 * suspect anything, and reloads the page.
 *
 * WHY THE SOCKET DIES UNNOTICED — both of the library's keepalives are `setInterval`-based: the
 * socket layer's `checkConnection` (every messageReconnectTimeout/10 = 3s, force-closing only
 * after 30s of silence) and y-protocols' 15s awareness re-announce. Niobe's workflow is to leave
 * the browser for the console agent, so the tab is BACKGROUNDED — where Chrome throttles timers
 * to roughly one tick per minute. The socket can therefore be dead for minutes while `status`
 * still says 'connected'. RECOVERY must not be timer-driven for the same reason, so it hangs off
 * the events that fire precisely when the member's attention (or the network) comes back —
 * `visibilitychange`, `online`, and the desktop shell's wake and window-shown messages. They reach
 * a room through lib/connectivity.ts, which first waits for the backend to answer and renews the
 * token: a room repaired on a woken laptop before DNS or the VPN is back would only fail again,
 * and one repaired with the expired token would be refused.
 *
 * WHY THE LIVE SCALARS ARE RE-READ, RATHER THAN THE EVENTS BEING BELIEVED (@hocuspocus/provider
 * 2.15.3) — `HocuspocusProviderWebsocket.checkConnection()` has two close paths, and the one taken
 * whenever the raw socket's `close` event never arrives (half-open TCP, dead tunnel, slept laptop:
 * `closeTries > 2`, the WebKit-bug workaround) calls `this.onClose({ event: { code: 4408, reason:
 * 'forced' } })` as a DIRECT METHOD CALL instead of `emit('close', …)`. `HocuspocusProvider.onClose`
 * — the one that does `this.synced = false` — is registered as a LISTENER on that 'close' event, so
 * it never runs: `provider.isSynced` stays `true` for the entire outage. On reconnect, SyncStep2
 * assigns `synced = true`, and `set synced` early-returns when the value is unchanged, so NO
 * 'synced' event is ever emitted. Any hook that had zeroed its OWN copy of `synced` (from the
 * 'disconnect' forward, or from `status !== 'connected'`) would then wait for an event that can
 * never come: a fully reconnected, fully synced room would show amber "Syncing…" and the stale
 * banner FOREVER — inverting the very bug this module exists to remove. So the events below are
 * treated as nothing more than "something may have changed"; the truth is re-read from the live
 * scalars (`status`, `synced`, and the raw socket's `readyState`) on every one of them.
 *
 * WHY THERE IS A ~5s TIMER, AND WHY IT IS NOT THE BUG ABOVE — it is a belt-and-braces RE-READ, not
 * a keepalive: nothing depends on it firing on time, and a throttled tick costs at most a late chip
 * update. The bug was using a timer AS the liveness mechanism. Do not "fix" this back into
 * trusting the 'synced' event.
 */

/** What the UI may honestly claim about a room. */
export interface RoomHealth {
  /** The provider's transport status, verbatim ('connecting' | 'connected' | 'disconnected'). */
  status: string
  /** Genuinely live: the raw socket is OPEN, the transport says connected, AND the document
   *  handshake landed. The ONLY condition under which the UI may show a green dot / say "Live". */
  healthy: boolean
  /** The member is being shown content we can no longer vouch for — the room synced at least
   *  once and is not healthy now. False during the very first connect, when there is nothing to
   *  be behind on yet. */
  stale: boolean
  /** True once this room's first handshake landed. What separates "still opening" from "was live
   *  and lost it", so the chip can say "Connecting…" instead of claiming a connection was lost. */
  everSynced: boolean
  /** The server REFUSED this room's handshake and it has not been accepted since — an expired
   *  credential, or a bid the member is not a member of. Distinct from `stale`, which needs a
   *  prior success: a room refused on its very first attempt has nothing to be behind on, yet is
   *  exactly as broken. Without this a blank editor under a reassuring "Connecting…" chip is
   *  indistinguishable from an empty part, which is how the desktop's idle-token bug presented. */
  authFailed: boolean
}

/** `WebSocket.OPEN` / `WebSocket.CLOSED`, inlined so this module needs no DOM global at import
 *  time. */
const SOCKET_OPEN = 1
const SOCKET_CLOSED = 3

/**
 * How long a probe waits for anything inbound before calling the socket a zombie. A live server
 * answers sync-step-1 in milliseconds; this is slack for a slow mobile link.
 */
export const PROBE_TIMEOUT_MS = 4000

/** Belt-and-braces re-read cadence — NOT a keepalive (see the module header). */
const RECHECK_MS = 5000

/** Provider events that mean "something about this room may have changed". Every one of them
 *  triggers a full re-read; none of them is taken as the state itself. */
const RECHECK_EVENTS = ['status', 'synced', 'message', 'unsyncedChanges', 'close', 'disconnect'] as const

/**
 * Events that end a liveness probe. 'message'/'unsyncedChanges' are the answer to the sync-step-1
 * the probe just sent — proof that the socket carries traffic. 'close'/'disconnect' mean the
 * transport already went down, so the provider's own reconnect owns it and there is nothing left
 * to prove.
 *
 * ('unsyncedChanges' also fires when the MEMBER types, so typing through the window can end a
 * probe early. That is the conservative failure — a zombie socket survives until the next trigger
 * — never a needless close.)
 */
const PROBE_END_EVENTS = ['message', 'unsyncedChanges', 'close', 'disconnect'] as const

const NOOP = (): void => undefined

/** Read one property off a value of unknown shape. Everything below the provider's documented
 *  surface is a library internal, so nothing is assumed to be there. */
function prop(target: unknown, key: string): unknown {
  if (typeof target !== 'object' || target === null) return undefined
  return (target as Record<string, unknown>)[key]
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return typeof prop(value, 'then') === 'function'
}

/** Call `target[method]()` when it is really a function. A version bump that renames or drops it
 *  degrades to "no forced resync" — never to an exception thrown inside an event handler. */
function invoke(target: unknown, method: string): void {
  const fn = prop(target, method)
  if (typeof fn !== 'function') return
  try {
    const result = (fn as (this: unknown) => unknown).call(target)
    // connect() hands back a retry promise that rejects when the attempt is abandoned; an
    // unhandled rejection would surface as an application error.
    if (isPromiseLike(result)) void Promise.resolve(result).catch(() => undefined)
  } catch {
    /* internals moved — recovery is best-effort by contract */
  }
}

/** Subscribe `handler` to several emitter events at once, guarded the same way as `invoke`: an
 *  emitter that lost `on`/`off` degrades to "no subscription", never to a throw inside an effect.
 *  Returns the unsubscriber. */
function listen(target: unknown, events: readonly string[], handler: () => void): () => void {
  const on = prop(target, 'on')
  const off = prop(target, 'off')
  if (typeof on !== 'function' || typeof off !== 'function') return NOOP
  type Bind = (this: unknown, event: string, fn: () => void) => unknown
  const attached: string[] = []
  for (const event of events) {
    try {
      ;(on as Bind).call(target, event, handler)
      attached.push(event)
    } catch {
      /* internals moved */
    }
  }
  return () => {
    for (const event of attached) {
      try {
        ;(off as Bind).call(target, event, handler)
      } catch {
        /* internals moved */
      }
    }
  }
}

/**
 * The shared socket layer (`HocuspocusProviderWebsocket` in @hocuspocus/provider 2.15): it owns
 * the raw WebSocket and `connect()`. Reached through `configuration` because the provider exposes
 * no accessor for it — hence the unknown-narrowing above.
 */
function socketLayer(provider: HocuspocusProvider): unknown {
  return prop(prop(provider, 'configuration'), 'websocketProvider')
}

/**
 * The raw socket's readyState, or `undefined` when it genuinely cannot be known (the layer moved).
 * An absent/null `webSocket` is NOT unknowable — the layer nulls it out on close, which means
 * there is no socket at all.
 */
function rawReadyState(layer: unknown): number | undefined {
  if (typeof layer !== 'object' || layer === null) return undefined
  const socket = prop(layer, 'webSocket')
  if (socket === null || socket === undefined) return SOCKET_CLOSED
  const state = prop(socket, 'readyState')
  return typeof state === 'number' ? state : undefined
}

/**
 * Is the raw socket OPEN right now? `whenUnknown` settles the case where the readyState cannot be
 * read at all: HEALTH assumes open (a moved internal must not be reported to the member as a
 * failure we did not observe), RECOVERY assumes closed (a needless handshake costs less than a
 * silent desync).
 */
function rawSocketIsOpen(layer: unknown, whenUnknown: boolean): boolean {
  const state = rawReadyState(layer)
  return state === undefined ? whenUnknown : state === SOCKET_OPEN
}

/**
 * Has this provider been DETACHED from the socket layer's routing map?
 *
 * A refused handshake does that: `permissionDeniedHandler` → `disconnect()` →
 * `providerMap.delete(name)`. The raw socket stays open (`preserveConnection` defaults true), so
 * every other signal here reads healthy while the provider can no longer receive a single message
 * — it is not in the map the layer routes by. `reauthenticateRoom()` is what puts it back.
 *
 * Unknowable → `false`, i.e. do nothing. The opposite default would replay `onOpen` (re-auth plus
 * a fresh sync-step-1) on every tab focus for a perfectly healthy room. Detection degrading is
 * survivable because it is not the only way back: Canvas subscribes to `onAuthenticationFailed`
 * and drives its own reconnect, independently of anything read here.
 */
function isDetached(provider: HocuspocusProvider): boolean {
  const map = prop(prop(socketLayer(provider), 'configuration'), 'providerMap')
  const name = prop(prop(provider, 'configuration'), 'name')
  if (!(map instanceof Map) || typeof name !== 'string') return false
  return map.get(name) !== provider
}

/** Close the raw socket, exactly as the library's own `checkConnection` does on a quiet one, so
 *  the provider's normal reconnect machinery (onClose → connect) takes over. */
function closeRawSocket(layer: unknown): void {
  invoke(prop(layer, 'webSocket'), 'close')
}

/**
 * Force a room back to life, and PROVE it.
 *
 * Deliberately does NOT call the layer's `checkConnection()`: every call increments the internal
 * `closeTries`, and at `> 2` that method takes its forced-close path (see the module header), so
 * calling it on each tab focus would drive a quiet socket into the pathological path far faster
 * than the library's own 3s interval ever would. Instead:
 *  - provider DETACHED → `reauthenticateRoom()`. Checked first because this state can wear any
 *    socket, including a perfectly open one, and no amount of syncing will fix it.
 *  - raw socket not OPEN → `connect()`, which also cancels a retry parked on an exponential
 *    backoff delay so the member does not wait it out. The handshake reports itself through
 *    'status'/'synced', so there is nothing to probe.
 *  - raw socket OPEN → `forceSync()` (sync-step-1) and then verify: a half-open TCP connection
 *    also reports OPEN, so only INBOUND traffic proves the socket is alive. Nothing within
 *    `PROBE_TIMEOUT_MS` means zombie — close the raw socket and let the reconnect machinery run.
 *
 * Returns a canceller for the pending verification (call it when the room closes or a new recovery
 * starts). `onSettled` fires when the verification reaches a verdict, so the caller can re-read
 * the provider — health is never demoted just because a probe STARTED, only when one fails.
 */
export function recoverRoom(provider: HocuspocusProvider, onSettled?: () => void): () => void {
  const layer = socketLayer(provider)
  if (isDetached(provider)) {
    reauthenticateRoom(provider)
    return NOOP
  }
  if (!rawSocketIsOpen(layer, false)) {
    // Re-attach as well as dial: a provider can be detached with the socket already down, and a
    // socket that reopens without it in the routing map syncs every OTHER room but this one.
    invoke(provider, 'connect')
    invoke(layer, 'connect')
    return NOOP
  }
  invoke(provider, 'forceSync')
  return probeInbound(provider, layer, onSettled)
}

/**
 * Get a REFUSED room re-authenticated. The only way back from an expired credential.
 *
 * Two things have to happen, and the second one is not obvious:
 *
 *  1. Re-attach the provider (`provider.connect()`), because the refusal detached it from the
 *     socket layer's routing map and a detached provider hears nothing.
 *  2. Force a NEW SOCKET. The refusal leaves the old one open (`preserveConnection`), and the
 *     server will not answer a second authentication message on it: @hocuspocus/server adds the
 *     room to `documentConnectionsEstablished` BEFORE calling `onAuthenticate` and only ever
 *     removes it in the connection's `onClose` — which never runs, because a refused handshake
 *     creates no connection. So a retry on the same socket is silently queued forever. Verified
 *     against a real Hocuspocus server, not read off the source.
 *
 * Attach first, close second: the provider must already be in the map when the replacement socket
 * opens, or its handshake is the one that gets missed. Closing normally (not with 4401) leaves
 * `shouldConnect` true, so the layer's own reconnect machinery dials again — the same mechanism the
 * zombie-socket probe below relies on.
 */
export function reauthenticateRoom(provider: HocuspocusProvider): void {
  const layer = socketLayer(provider)
  invoke(provider, 'connect')
  if (rawSocketIsOpen(layer, false)) closeRawSocket(layer)
  // Already down: dial now rather than let the member wait out a parked exponential backoff.
  else invoke(layer, 'connect')
}

/** The verification half of `recoverRoom` — see its doc comment. */
function probeInbound(
  provider: HocuspocusProvider,
  layer: unknown,
  onSettled?: () => void,
): () => void {
  let done = false
  let timer: ReturnType<typeof setTimeout> | undefined
  let stopListening: () => void = NOOP

  /** End the probe. `zombie` closes the socket; `null` is the caller cancelling (room closed, or
   *  a fresh recovery superseding this one) and must change nothing. */
  const finish = (verdict: 'answered' | 'zombie' | null): void => {
    if (done) return
    done = true
    if (timer !== undefined) clearTimeout(timer)
    stopListening()
    if (verdict === null) return
    if (verdict === 'zombie') closeRawSocket(layer)
    onSettled?.()
  }

  timer = setTimeout(() => finish('zombie'), PROBE_TIMEOUT_MS)
  // Subscribed AFTER forceSync() ON PURPOSE: forceSync emits 'unsyncedChanges' SYNCHRONOUSLY
  // (resetUnsyncedChanges), so a listener attached first would answer its own probe instantly.
  stopListening = listen(provider, PROBE_END_EVENTS, () => finish('answered'))
  return () => finish(null)
}

interface Snapshot {
  status: string
  synced: boolean
  /** The raw socket is OPEN. A socket that is not open is not live, whatever `status` claims. */
  socketOpen: boolean
  everSynced: boolean
}

const CLOSED: Snapshot = { status: 'disconnected', synced: false, socketOpen: false, everSynced: false }

/** Read the whole truth off the live provider. `everSynced` only ever ratchets up. */
function readSnapshot(provider: HocuspocusProvider | null, everSynced: boolean): Snapshot {
  if (!provider) return CLOSED
  const status = prop(provider, 'status')
  const synced = prop(provider, 'synced') === true
  return {
    status: typeof status === 'string' ? status : 'connecting',
    synced,
    socketOpen: rawSocketIsOpen(socketLayer(provider), true),
    everSynced: everSynced || synced,
  }
}

function unchanged(a: Snapshot, b: Snapshot): boolean {
  return (
    a.status === b.status &&
    a.synced === b.synced &&
    a.socketOpen === b.socketOpen &&
    a.everSynced === b.everSynced
  )
}

/**
 * Track a room's real health and repair it on the events that mean "the member is back".
 *
 * `provider` is null while no room is open. Pass the SAME instance across renders (it is created
 * once per room in an effect) — every subscription is keyed on it.
 */
export function useRoomHealth(provider: HocuspocusProvider | null): RoomHealth {
  // Reset-state-on-prop-change: a snapshot belongs to ONE provider. Its identity is kept in state
  // beside it and the snapshot is re-read DURING render when they disagree, so not even a discarded
  // render can report the previous room's health. Seeding in an effect instead let the first frame
  // after a part switch show green "Live" over a room that had not connected — and fake a
  // "Connection restored" transition as the seed landed.
  const [tracked, setTracked] = useState<HocuspocusProvider | null>(provider)
  const [snap, setSnap] = useState<Snapshot>(() => readSnapshot(provider, false))
  // Whether the server refused this room. Event-sourced rather than read off a scalar because
  // `isAuthenticated` is equally false while a handshake is merely in progress — only the refusal
  // itself separates "not yet" from "no". Both events are real `emit`s (unlike the 'close' the
  // module header warns about), and the scalar re-read below is what keeps them honest.
  const [refused, setRefused] = useState(false)
  const current = tracked === provider ? snap : readSnapshot(provider, false)
  if (tracked !== provider) {
    setTracked(provider)
    setSnap(current)
    setRefused(false)
  }

  const recheck = useCallback((): void => {
    setSnap((prev) => {
      const next = readSnapshot(provider, prev.everSynced)
      return unchanged(prev, next) ? prev : next
    })
  }, [provider])

  useEffect(() => {
    if (!provider) return
    // A 'status' event can have fired between the render-phase read and this subscription (the
    // provider attaches synchronously in its constructor).
    recheck()
    const stopListening = listen(provider, RECHECK_EVENTS, recheck)
    const timer = setInterval(recheck, RECHECK_MS)
    return () => {
      stopListening()
      clearInterval(timer)
    }
  }, [provider, recheck])

  // The refusal latch, kept beside the scalars rather than inside them (see `refused` above).
  useEffect(() => {
    if (!provider) return
    const stopFailed = listen(provider, ['authenticationFailed'], () => setRefused(true))
    const stopOk = listen(provider, ['authenticated'], () => setRefused(false))
    return () => {
      stopFailed()
      stopOk()
    }
  }, [provider])

  // Self-healing, driven by attention and network events rather than by a keepalive timer — see
  // the module header for why a timer cannot be the mechanism here. The events themselves are
  // heard by lib/connectivity.ts, which calls back here as step 4 of its ordered recovery: once the
  // backend answers and the token has been renewed.
  useEffect(() => {
    if (!provider) return
    let cancelProbe: () => void = NOOP
    const recover = (): void => {
      cancelProbe()
      recheck()
      cancelProbe = recoverRoom(provider, recheck)
    }
    const stopRecover = onRoomsRecover(recover)
    return () => {
      cancelProbe()
      stopRecover()
    }
  }, [provider, recheck])

  const healthy = current.status === 'connected' && current.synced && current.socketOpen
  return {
    status: current.status,
    healthy,
    stale: current.everSynced && !healthy,
    everSynced: current.everSynced,
    authFailed: refused && !healthy,
  }
}
